import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import EditIcon from "@mui/icons-material/Edit";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import {
  DynamicMenuItem,
  MenuItemConfig,
} from "components/Menuitem/DynamicMenuItem";
import { app_modules } from "helpers/apps_modules_list";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  downloadOrderPdf,
  exportOrderCSV,
  fetchOrder,
} from "slices/Order/thunk";

import TableContainer2 from "Common/Tables/ObjectFiltersTable/TableContainer2";
import { Axios } from "api/axios";
import ActionButton from "components/Buttons/ActionButton";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import OrderStatusBadge from "components/StatusBadge/OrderStatusBadge";
import { api } from "helpers/api_url";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";

const Order = () => {
  document.title = "Order List";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();
  const selectPRs = createSelector(
    (state: any) => state.orderReducer,
    (data) => ({
      list: data.list,
      rowCount: data.rowCount,
      loading: data.loading,
      error: data.error,
    })
  );

  const { list, rowCount, loading, error } = useSelector(selectPRs);

  // Use custom hook to fetch and access permissions
  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.order?.app_name,
    app_modules?.order?.modules?.order
  );

  // Use the preserved table state hook
  const tableState = usePreservedTableState({
    key: "order",
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue: colValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue: setColValue,
    resetState,
  } = tableState;

  const [data, setData] = useState([]);
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  const isFirstRender = useRef(true);

  // Refs to store latest values for use in callbacks
  const latestStateRef = useRef({
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    colValue,
  });

  // Update refs whenever values change
  useEffect(() => {
    latestStateRef.current = {
      pageIndex,
      pageSize,
      sortField,
      sortDirection,
      search,
      colValue,
    };
  }, [pageIndex, pageSize, sortField, sortDirection, search, colValue]);
  useEffect(() => {
    dispatch(
      fetchOrder({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: colValue,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    colValue,
  ]);

  useEffect(() => {
    setData(list);
  }, [list]);

  // Mark first render as complete
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, []);

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  // Debounce search input to actual search
  // Only update search if searchInput actually changed (not on initial restore)
  useEffect(() => {
    // Skip if searchInput matches search (already in sync, likely from restore)
    if (searchInput === search) {
      return;
    }

    const delayDebounce = setTimeout(() => {
      const trimmedInput = searchInput.trim();

      const dateMatch = trimmedInput.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

      if (dateMatch) {
        const [, day, month, year] = dateMatch;
        const isoDate = `${year}-${month}-${day}`;
        setSearch(isoDate);
      } else {
        setSearch(trimmedInput);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch, search]);

  const handleExport = async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      await exportOrderCSV({
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: colValue,
      });
      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export completed successfully.",
      });
    } catch (error: any) {
      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: error?.response?.data?.message || "Failed to export order data.",
      });
      console.error("Export error:", error);
    }
  };

  const handleRequestDispatch = useCallback((order_id: string) => {
    showCustomSwal({
      title: "Requesting...",
      text: "Please wait while we process your request.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    Axios.patch(`${api?.order?.request_dispatch}/${order_id}`, {
      status: "requested for dispatch",
    })
      .then((result) => {
        // Replace loading modal with success modal
        showCustomSwal({
          icon: "success",
          title: "Request Sent",
          text: "Request for dispatch completed successfully.",
        });

        // Refetch with current filters and stay on same page using latest ref values
        const currentState = latestStateRef.current;
        dispatch(
          fetchOrder({
            page: currentState.pageIndex + 1,
            pageSize: currentState.pageSize,
            sort: currentState.sortField,
            order: currentState.sortField
              ? (currentState.sortDirection as "asc" | "desc")
              : "",
            search: currentState.search,
            filters: currentState.colValue,
          })
        );
      })
      .catch((err) => {
        // Replace loading modal with error modal
        showCustomSwal({
          icon: "error",
          title: "Failed to request",
          text: err?.response?.data?.message || "Failed request for dispatch.",
        });
        console.error("Request dispatch error:", err);
      });
  }, [dispatch]);

  const handleDownloadPDF = useCallback(
    async (order_id: string, order_no?: string) => {
      if (!order_id) {
        toast.error("Order ID not found");
        return;
      }

      showCustomSwal({
        title: "Downloading PDF...",
        text: "Please wait while we prepare your order invoice.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const { blob, filename } = await dispatch(
          downloadOrderPdf({ id: order_id, orderNumber: order_no })
        ).unwrap();

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        Swal.close();
        toast.success("PDF downloaded");
      } catch (error: any) {
        console.error("Order PDF download error:", error);
        // Extract error message from various possible error structures
        const message =
          error?.payload?.message ||
          error?.payload?.error ||
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to download order PDF. Please contact support if the issue persists.";
        Swal.close();
        showCustomSwal({
          icon: "error",
          title: "Download Failed",
          text: message,
        });
      }
    },
    [dispatch]
  );

  const handleCancelOrder = useCallback(
    (order_id: string) => {
      navigate(`/cancel-order/${order_id}`);
    },
    [navigate]
  );

  // Create menu items for DynamicMenuItem
  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      {
        icon: EditIcon,
        label: "Edit",
        permission: (row: any) => {
          return !!(
            canUpdate && row?.order_status?.toLowerCase() === "pending dispatch"
          );
        },
        onClick: (row: any) => {
          navigate(`/edit-order/${row.order_id}`, {
            state: row,
          });
        },
        color: "themeColor",
      },
      {
        icon: VisibilityIcon,
        label: "View Details",
        permission: canView,
        link: (row: any) => `/view-order/${row.order_id}`,
        color: "themeColor",
      },
      {
        icon: PictureAsPdfIcon,
        label: "Download PDF",
        permission: canView,
        onClick: (row: any) => handleDownloadPDF(row.order_id, row.order_no),
        color: "themeColor",
      },
      {
        icon: LocalShippingIcon,
        label: "Request for Dispatch",
        permission: (row: any) => {
          return !!(
            canUpdate &&
            row?.order_status?.toLowerCase() !== "cancelled" &&
            row?.order_status?.toLowerCase() !== "requested for dispatch" &&
            row?.order_status?.toLowerCase() !== "dispatched" &&
            row?.order_status?.toLowerCase() !== "partially dispatched"
          );
        },
        onClick: (row: any) => {
          if (row?.order_status?.toLowerCase() !== "cancelled") {
            handleRequestDispatch(row.order_id);
          }
        },
        color: "themeColor",
      },
      {
        icon: CloseOutlinedIcon,
        label: "Cancel Order",
        permission: (row: any) => {
          return !!(
            canUpdate &&
            (row?.order_status?.toLowerCase() === "pending dispatch" ||
              row?.order_status?.toLowerCase() === "partially cancelled")
          );
        },
        onClick: (row: any) => {
          handleCancelOrder(row.order_id);
        },
        color: "error",
      },
    ];
    return items;
  }, [canView, canUpdate, navigate, handleCancelOrder, handleDownloadPDF, handleRequestDispatch]);

  const DynamicAction = useMemo(() => {
    // Only show Action column if user has at least one permission
    const hasPermission = canUpdate || canView;

    if (!hasPermission) return [];

    return [
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <DynamicMenuItem row={row.original} menuItems={menuItems} />
        ),
      },
    ];
  }, [canUpdate, canView, menuItems]);

  const columns = useMemo(
    () => [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Order No.",
        accessorKey: "order_no",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/order/dropdown/order_no",
        cell: ({ row }: { row: any }) => {
          return row?.original?.order_no || "-";
        },
      },
      {
        header: "Quotation No.",
        accessorKey: "quotation_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/order/dropdown/quotation_number",
        cell: ({ row }: { row: any }) => {
          return row?.original?.quotation?.quotation_number || "-";
        },
      },
      {
        header: "Order Date",
        accessorKey: "order_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const date = row.original.order_date
            ? new Date(row.original.order_date).toISOString().split("T")[0]
            : "N/A";
          return `${date}`;
        },
      },

      {
        header: "Customer Name",
        accessorKey: "customer_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/order/dropdown/customer_name",
        cell: ({ row }: { row: any }) => {
          return row?.original?.customer?.customer_name || "-";
        },
      },
      {
        header: "Dispatch from warehouse",
        accessorKey: "warehouse_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/order/dropdown/warehouse_name",
        cell: ({ row }: { row: any }) => {
          return row?.original?.warehouse?.warehouse_name || "-";
        },
      },
      {
        header: "Order Status",
        accessorKey: "order_status",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/order/dropdown/order_status",
        cell: ({ row }: { row: any }) => {
          return (
            <OrderStatusBadge status={row?.original?.order_status || "-"} />
          );
        },
      },

      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user?.username ?? "N/A";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "N/A";
          return `${name}-${date}`;
        },
      },

      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        filterType: "date" as const,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user?.username ?? "N/A";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "N/A";
          return `${name}-${date}`;
        },
      },
      ...DynamicAction,
    ],
    [DynamicAction]
  );

  return (
    <ListingPageContainer>
      <Card>
        <Card.Header>
          <Row className="align-items-center g-2">
            <Col lg={3} className="me-auto">
              <h6 className="card-title mb-0">Order</h6>
            </Col>

            {/* Search Input Box (reusable)*/}
            <Col xl={2} md={3}>
              <SearchBox value={searchInput} onChange={setSearchInput} />
            </Col>

            <Col className="col-md-auto">
              <div className="hstack gap-2">
                {canView && (
                  <ActionButton
                    label="Export"
                    icon="bi bi-box-arrow-up"
                    handleClick={handleExport}
                    variant="light"
                  />
                )}

                {canCreate && (
                  <ActionButton
                    label="Create Order"
                    icon="bi bi-plus-circle"
                    navigateLink="/add-order"
                    variant="primary"
                  />
                )}
              </div>
            </Col>

            <Col className="col-md-auto"></Col>
          </Row>
        </Card.Header>
        <Card.Body className="mt-3">
          <LoaderErrorWrapper loading={loading} error={error}>
            <TableContainer2
              columns={columns}
              data={data}
              totalRecords={rowCount}
              pageIndex={pageIndex}
              pageSize={pageSize}
              onPageChange={setPageIndex}
              onPageSizeChange={setPageSize}
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              colValue={setColValue}
              sorting={sorting}
              onSortingChange={setSorting}
              isBordered={false}
              isPagination={true}
              isGlobalFilter={false}
              SearchPlaceholder="Search ..."
              manualSorting={true}
            />
          </LoaderErrorWrapper>
        </Card.Body>
      </Card>
    </ListingPageContainer>
  );
};

export default Order;
